import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, put } from "./api";
import type { PaymentFlow, PaymentFlowSetting } from "./types";

/**
 * The public payment flow — pay-ui spec §11.3, and the one setting an operator can
 * change without a deploy.
 *
 * ## Why this file is mostly sentences
 *
 * The switch itself is one string with two values. What makes it hard is that both
 * values are defensible and the difference between them only exists on a stranger's
 * bank statement: under `purchase` the card is debited for the full amount and the
 * unused part is refunded days later; under `pre_auth` the same amount is authorised
 * and never taken, and only the energy used is captured. An operator choosing between
 * two words with no explanation will pick the one that sounds tidier.
 *
 * So the copy is the feature, and it lives here rather than inline in the page,
 * where it can be asserted against.
 */

export const PAYMENT_FLOW_KEY = ["settings", "payment-flow"] as const;

export interface FlowOption {
  readonly value: PaymentFlow;
  /** What the operator is choosing, phrased as the action taken on the card. */
  readonly label: string;
  /** Two sentences: what happens now, and what happens to the part not used. */
  readonly summary: string;
  /**
   * Present when this deployment cannot offer the option at all. The server refuses
   * it with a 409, so presenting it as selectable would turn a deployment fact into
   * what looks like a failed save.
   */
  readonly disabledReason?: string;
}

/**
 * `pre_auth` needs the merchant-portal credential the rail's completion and
 * cancellation calls are signed with; without it the provider reports
 * `supportsPreAuth: false` and every order would silently fall back. Named as a
 * capability rather than a missing variable, because an operator reading this cannot
 * fix it from the panel either way — what they need to know is that the choice is
 * not theirs to make here.
 */
const NO_HOLD_REASON =
  "This deployment's payment rail cannot hold money. Ask whoever runs the server — it needs the merchant credential that authorises holds before this can be offered.";

export function flowOptions(setting: PaymentFlowSetting): readonly FlowOption[] {
  return [
    {
      value: "purchase",
      label: "Take the money",
      summary:
        "The driver's card is charged the full amount before the charge starts. What they do not use is refunded afterwards, which can take days to appear on their statement.",
      // Every rail this panel can be pointed at can take a payment. There is no
      // deployment on which this option is unavailable, so it never carries a reason.
    },
    {
      value: "pre_auth",
      label: "Hold the money",
      summary:
        "The driver's card is authorised for the full amount and nothing is taken. Only the energy actually used is captured when the charge ends; the rest of the hold lapses on its own.",
      ...(setting.preAuthAvailable ? {} : { disabledReason: NO_HOLD_REASON }),
    },
  ];
}

export interface Provenance {
  readonly label: string;
  readonly detail: string;
  /**
   * The stored value disagrees with what this server was started with. Worth
   * surfacing on its own: it is the state where editing the environment file changes
   * nothing, which is otherwise a confusing afternoon.
   */
  readonly overridesConfigured: boolean;
}

export function provenanceOf(setting: PaymentFlowSetting): Provenance {
  if (setting.source === "configured") {
    return {
      label: "Deployment default",
      detail:
        "Nobody has chosen a flow here. This is PUBLIC_PAYMENT_FLOW as the server was started with, and it will follow that variable if it changes.",
      overridesConfigured: false,
    };
  }

  const overrides = setting.flow !== setting.configured;

  return {
    label: "Chosen here",
    detail: overrides
      ? `Stored in the database, which overrides this server's PUBLIC_PAYMENT_FLOW of "${setting.configured}". Changing that variable will not move it back.`
      : "Stored in the database. It happens to match this server's PUBLIC_PAYMENT_FLOW, but it is now pinned independently of it.",
    overridesConfigured: overrides,
  };
}

/**
 * Whether choosing this flow would write nothing new.
 *
 * Deliberately false for the effective flow while `source` is `configured`, even
 * though the value on the wire is identical. Saving it there is a real act: it pins
 * the flow against a later edit of `PUBLIC_PAYMENT_FLOW`, and it puts a named
 * operator and a timestamp in the audit trail where there was previously only a
 * default nobody chose.
 */
export function isAlreadyPinned(chosen: PaymentFlow, setting: PaymentFlowSetting): boolean {
  return setting.source === "stored" && chosen === setting.flow;
}

/**
 * What saying yes will do, or `undefined` when the flow is not moving.
 *
 * Both sentences end the same way, and that second sentence is the important one:
 * the flow is snapshotted onto an order at creation and every later decision reads
 * that column, so this switch cannot change the meaning of money already taken or
 * held. It is what makes flipping it mid-incident safe, and an operator hesitating
 * over the button is exactly who needs to be told.
 */
export function consequenceOf(from: PaymentFlow, to: PaymentFlow): string | undefined {
  if (from === to) return undefined;

  const change =
    to === "pre_auth"
      ? "New orders will hold the driver's money instead of taking it."
      : "New orders will charge the driver in full and refund what they do not use.";

  return `${change} Orders already in flight keep the flow they were created under, and nothing about money already taken or held changes.`;
}

export function usePaymentFlow() {
  return useQuery({
    queryKey: PAYMENT_FLOW_KEY,
    queryFn: () => get<PaymentFlowSetting>("/settings/payment-flow"),
    /**
     * No polling. This changes when somebody in this panel changes it, and the
     * mutation seeds the answer — a background refetch would only ever repeat what
     * is already on screen.
     */
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useSetPaymentFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flow: PaymentFlow) =>
      put<{ flow: PaymentFlow; updatedAt: string }>("/settings/payment-flow", { flow }),
    /**
     * Invalidated rather than seeded from the response. The `PUT` answers with the
     * value and the timestamp but not with `source` or `preAuthAvailable`, and
     * patching two of five fields into a cached object is how a panel ends up showing
     * "Deployment default" next to a value somebody just stored.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENT_FLOW_KEY });
    },
  });
}
